import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../lib/icons';
import { highlight } from '../../lib/richtext';

/**
 * Code in a message: monospace, as written (its own scroll sideways, never the page's), lightly
 * coloured, with Copy on a bar under it (the message's own tools appear over its top corner on
 * hover). Shown as text, never as HTML.
 */
export function CodeBlock({ code, lang }: { code: string; lang: string | null }) {
  const tokens = useMemo(() => highlight(code), [code]);
  const pre = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState<'' | 'yes' | 'selected'>('');
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = async () => {
    window.clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(code);
      setCopied('yes');
    } catch {
      // The browser didn't allow it: the code is selected instead, to copy by hand.
      const el = pre.current;
      const range = document.createRange();
      if (el) range.selectNodeContents(el);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      setCopied('selected');
    }
    timer.current = window.setTimeout(() => setCopied(''), 2500);
  };
  return (
    <div className="msg-code">
      <pre ref={pre} tabIndex={0} aria-label={lang ? `${lang} code` : 'Code'}>
        <code>{tokens.map((t, i) => (t.kind ? <span key={i} className={`tok-${t.kind}`}>{t.text}</span> : t.text))}</code>
      </pre>
      <div className="msg-code-bar">
        <span className="msg-code-lang">{lang ?? 'Code'}</span>
        <button type="button" className="msg-code-copy" onClick={() => void copy()} title={copied === 'selected' ? 'Your browser didn’t allow copying: the code is selected, so copy it from there.' : 'Copy the code'}>
          <Icon name={copied === 'yes' ? 'check' : 'copy'} size={13} strokeWidth={2.2} />
          <span aria-live="polite">
            {copied === 'yes' ? 'Copied' : copied === 'selected' ? 'Selected' : 'Copy'}
            <span className="sr-only"> the code</span>
          </span>
        </button>
      </div>
    </div>
  );
}
